import * as z from "zod/v4";

export enum TimeUnit {
  YEAR = "year",
  MONTH = "month",
  WEEK = "week",
  DAY = "day",
  HOUR = "hour",
  MINUTE = "minute",
  SECOND = "second",
  MILLISECOND = "millisecond",
}

const TIME_UNIT_SECONDS: Record<TimeUnit, number> = {
  [TimeUnit.MILLISECOND]: 1 / 1000,
  [TimeUnit.SECOND]: 1,
  [TimeUnit.MINUTE]: 60,
  [TimeUnit.HOUR]: 60 * 60,
  [TimeUnit.DAY]: 60 * 60 * 24,
  [TimeUnit.WEEK]: 60 * 60 * 24 * 7,
  // Calendar-approximate: 30 days. Don't use for calendar-precise math.
  [TimeUnit.MONTH]: 60 * 60 * 24 * 30,
  // Calendar-approximate: 365 days (ignores leap years).
  [TimeUnit.YEAR]: 60 * 60 * 24 * 365,
};

export function durationToSeconds(value: number, unit: TimeUnit): number {
  return value * TIME_UNIT_SECONDS[unit];
}

export function getTimeUnitName(unit: TimeUnit, value: number) {
  switch (unit) {
    case TimeUnit.YEAR:
      return value === 1 ? "year" : "years";
    case TimeUnit.MONTH:
      return value === 1 ? "month" : "months";
    case TimeUnit.WEEK:
      return value === 1 ? "week" : "weeks";
    case TimeUnit.DAY:
      return value === 1 ? "day" : "days";
    case TimeUnit.HOUR:
      return value === 1 ? "hour" : "hours";
    case TimeUnit.MINUTE:
      return value === 1 ? "minute" : "minutes";
    case TimeUnit.SECOND:
      return value === 1 ? "second" : "seconds";
    case TimeUnit.MILLISECOND:
      return value === 1 ? "millisecond" : "milliseconds";
  }
}

export const zMilliseconds = z.number().brand<"milliseconds">();
export type Milliseconds = z.infer<typeof zMilliseconds>;

export const zSeconds = z.number().brand<"seconds">();
export type Seconds = z.infer<typeof zSeconds>;

export const zMinutes = z.number().brand<"minutes">();
export type Minutes = z.infer<typeof zMinutes>;

export const zHours = z.number().brand<"hours">();
export type Hours = z.infer<typeof zHours>;

export const zDays = z.number().brand<"days">();
export type Days = z.infer<typeof zDays>;

export const zWeeks = z.number().brand<"weeks">();
export type Weeks = z.infer<typeof zWeeks>;

export const zMonths = z.number().brand<"months">();
export type Months = z.infer<typeof zMonths>;

export const zYears = z.number().brand<"years">();
export type Years = z.infer<typeof zYears>;
